import {
  useMutation,
  QueryOptions,
  MutationOptions,
  UseMutationReturnType,
} from "@tanstack/vue-query";
import type { AxiosRequestConfig } from "axios";
import api from "./axiosConfig";
import { generatePayloadNew, isBlob } from "./utils";
import { pathOr } from "ramda";
import { useHooks } from "./hooks";

type MethodsType = "get" | "post" | "patch" | "put" | "delete";

export type MethodOptions<
  TData = unknown,
  TError = unknown,
  TVariables = MutationFunctionInput
> = {
  get: QueryOptions<TData, TError, TData, any>;
  post: MutationOptions<TData, TError, TVariables, unknown>;
  put: MutationOptions<TData, TError, TVariables, unknown>;
  patch: MutationOptions<TData, TError, TVariables, unknown>;
  delete: MutationOptions<TData, TError, TVariables, unknown>;
};

export type MutationFunctionInput = {
  body?: Record<string, any>;
  queryString?: Record<string, string>;
  replacement?: string[];
  url?: string;
};

const localLang = localStorage.getItem("localLang");
const envLang = import.meta.env.VUE_APP_I18N_LOCALE;
const locale = localLang || envLang || "fa";

function useMyMutation<
  TData = unknown,
  TError = unknown,
  TVars = MutationFunctionInput
>(
  url: string,
  method: MethodsType,
  options: MutationOptions<TData, TError, TVars, unknown>,
  mapping: Record<string, string> | undefined,
  transformResponse: ((data: any) => any) | undefined,
  middlewareOptions: any,
  axiosOptions?: AxiosRequestConfig
) {
  const { toastAlert } = useHooks();
  const originalOnError = options.onError;
  const localOnError = async (error: any, variables: any, context: unknown) => {
    if (middlewareOptions.showError) {
      let message = "";
      if (error?.response?.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          const json = JSON.parse(reader.result as string);
          message = json?.message || json?.data?.message;
          toastAlert(message, "error");
        };
        reader.readAsText(error.response.data);
      } else {
        /* start log error for debugging */
        if (import.meta.env.DEV) {
          console.log(`
            =================================
            ${error.response?.config?.method.toUpperCase()}: ${
            error.response?.config?.url
          }
            STATUS: ${error.response?.status}
            MESSAGE: ${error.response?.data?.message}
    
            PAYLOAD: ${error.response?.config?.data}
            =================================
            `);
        }
        /* end log error for debugging */

        message = pathOr("", ["response", "data", "message"], error);
        if (Array.isArray(message) && message.length) {
          message.forEach((msg: any) => toastAlert(msg, "error"));
        } else {
          message && toastAlert(message, "error");
        }
      }
    }

    if (originalOnError) {
      (
        originalOnError as (
          error: TError,
          variables: TVars,
          context: unknown
        ) => void
      )(error, variables, context);
    }
  };

  return useMutation<any, TError, TVars, unknown>({
    mutationKey: options.mutationKey || [url],
    mutationFn: async (variables: any) => {
      let finalUrl = variables.url ? variables.url : url;
      let payload = {};

      if (variables.replacement) {
        const parsedReplacement = variables.replacement;
        for (const key in parsedReplacement) {
          if (Object.prototype.hasOwnProperty.call(parsedReplacement, key)) {
            finalUrl = finalUrl?.replace(`{${key}}`, parsedReplacement[key]);
          }
        }
      }

      if ((variables as any).payload) {
        payload = generatePayloadNew((variables as any).payload, mapping);
      }

      const response = await api({
        url: finalUrl.replace("/fa/", `/${locale}/`),
        method,
        data: { ...payload },
        params: { ...variables.queryString },
        ...axiosOptions,
      });
      const axiosConfigs = pathOr({}, ["config"], response);
      const data = pathOr({ data: {} }, ["data"], response);
      if (isBlob(data)) {
        if (typeof transformResponse === "function") {
          return transformResponse(data);
        }
        return data;
      } else {
        const { data: resultData, ...configs } = data;
        const finalResult = {
          data: Array.isArray(resultData)
            ? resultData
            : { ...resultData, configs },
          configs: { ...configs, axiosConfigs: { ...axiosConfigs } },
        };

        if (typeof transformResponse === "function") {
          return transformResponse(finalResult);
        }
        return finalResult;
      }
    },
    onError: localOnError,
    ...options,
  });
}

export function fetchMiddlewareQuery<
  TData = unknown,
  TError = unknown,
  TVars = MutationFunctionInput
>(params: {
  url: string;
  method: MethodsType;
  immediate?: boolean;
  options: MutationOptions<TData, TError, TVars, unknown>;
  mapping?: Record<string, string>;
  axiosOptions?: AxiosRequestConfig;
  middlewareOptions?: any;
  transformResponse?: (data: any) => any;
}): UseMutationReturnType<TData, TError, TVars, unknown>;

export function fetchMiddlewareQuery(params: any): any {
  const {
    url,
    method,
    options,
    mapping,
    axiosOptions,
    transformResponse,
    middlewareOptions = { showError: true },
  } = params;

  return useMyMutation(
    url,
    method,
    options,
    mapping,
    transformResponse,
    middlewareOptions,
    axiosOptions
  );
}

export default fetchMiddlewareQuery;
